import { useRef, useState, useCallback, useEffect } from 'react';

interface UseDragScrollOptions {
  scrollSpeed?: number; // Multiplier for scroll speed (default: 2)
  direction?: 'horizontal' | 'vertical' | 'both'; // Scroll direction (default: 'horizontal')
}

/**
 * Custom hook to enable drag-to-scroll functionality on any scrollable element
 * Perfect for tables with horizontal overflow
 * 
 * @param options - Configuration options
 * @returns ref - Attach this ref to the scrollable container element
 * 
 * @example
 * const scrollRef = useHorizontalDragScroll();
 * return <div ref={scrollRef} className="overflow-x-auto">...</div>
 */
export const useHorizontalDragScroll = <T extends HTMLElement = HTMLDivElement>(
  options: UseDragScrollOptions = {}
) => {
  const { scrollSpeed = 2, direction = 'horizontal' } = options;
  
  const ref = useRef<T>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (!ref.current) return;
    
    // Don't interfere with text selection or button clicks
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'A' ||
      target.closest('button') ||
      target.closest('a')
    ) {
      return;
    }

    setIsDragging(true);
    setStartX(e.pageX - ref.current.offsetLeft);
    setStartY(e.pageY - ref.current.offsetTop);
    setScrollLeft(ref.current.scrollLeft);
    setScrollTop(ref.current.scrollTop);
    
    if (ref.current) {
      ref.current.style.cursor = 'grabbing';
      ref.current.style.userSelect = 'none';
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (ref.current) {
      ref.current.style.cursor = 'grab';
      ref.current.style.userSelect = 'auto';
    }
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !ref.current) return;
    e.preventDefault();
    
    if (direction === 'horizontal' || direction === 'both') {
      const x = e.pageX - ref.current.offsetLeft;
      const walkX = (x - startX) * scrollSpeed;
      ref.current.scrollLeft = scrollLeft - walkX;
    }
    
    if (direction === 'vertical' || direction === 'both') {
      const y = e.pageY - ref.current.offsetTop;
      const walkY = (y - startY) * scrollSpeed;
      ref.current.scrollTop = scrollTop - walkY;
    }
  }, [isDragging, startX, startY, scrollLeft, scrollTop, scrollSpeed, direction]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Set initial cursor style
    element.style.cursor = 'grab';

    // Add event listeners
    element.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseUp);

    // Cleanup
    return () => {
      element.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [handleMouseDown, handleMouseUp, handleMouseMove]);

  return ref;
};
